import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';

/**
 * The parent type each built-in role type is grouped under, for the Add/Edit
 * Role permissions table. One entry per role key; roles sharing a parent type
 * share a row (Manager toggle + Viewer toggle).
 *
 * This is deliberately an explicit map rather than something derived from the
 * role's `auth.role_type_resource_type` grants (COI-700). A role holds a grant
 * per resource type it touches — `RiskManager` holds twelve, including
 * `acceptance` and `appetite` — so there is no "the" resource type to read off,
 * and the screen used to take `resourceTypes[0]`. That produced two bugs at
 * once: rows labelled after a satellite type (Risks appeared as "Acceptances",
 * Issues as "Causes"), and a hard crash as soon as the first grant happened to
 * be a type with no entity-info entry. `SettingsManager` is the role that
 * triggered it — promoted to `InstanceRoleKey = 'Owner'` by
 * 1779432679000_rsp-5847-standard-readonly-catalogues so that perm_v2 could
 * reserve NULL for read-only, which swept it in with its alphabetically-first
 * grant `business_area`. COI-700 dropped it from the screen; COI-818 puts it
 * back deliberately, grouped under `settings` rather than whatever its grants
 * happen to sort to.
 *
 * Not every entry is a register. `SettingsManager` governs the Settings area
 * and has no Viewer counterpart — the ticket is explicit that a settings
 * viewer has no use — which the table already handles by rendering an empty
 * Viewer cell for a group with no Viewer role.
 *
 * Adding a new register: add both its Manager and Viewer role keys here, and
 * make sure the parent type has an entry in `useEntityInfo` (plus a
 * `permissionLabels` override if the module name differs from the taxonomy
 * term). A role absent from this map is not offered as a custom-role
 * permission — the catalogue growing by migration can no longer change this
 * screen.
 */
const PERMISSION_GROUP_PARENT_TYPES: Record<string, Parent_Type_Enum> = {
  ActionManager: Parent_Type_Enum.Action,
  ActionViewer: Parent_Type_Enum.Action,
  AssessmentManager: Parent_Type_Enum.Assessment,
  AssessmentViewer: Parent_Type_Enum.Assessment,
  // COI-818. Seeded by 1779376677000_create_asset with Owner/Reader instance
  // roles but never added here, so the pair was grantable nowhere.
  AssetManager: Parent_Type_Enum.Asset,
  AssetViewer: Parent_Type_Enum.Asset,
  ComplianceManager: Parent_Type_Enum.Obligation,
  ComplianceViewer: Parent_Type_Enum.Obligation,
  ControlManager: Parent_Type_Enum.Control,
  ControlViewer: Parent_Type_Enum.Control,
  CustomDataSourceManager: Parent_Type_Enum.CustomDatasource,
  CustomDataSourceViewer: Parent_Type_Enum.CustomDatasource,
  IndicatorManager: Parent_Type_Enum.Indicator,
  IndicatorViewer: Parent_Type_Enum.Indicator,
  InternalAuditManager: Parent_Type_Enum.InternalAuditEntity,
  InternalAuditViewer: Parent_Type_Enum.InternalAuditEntity,
  IssueManager: Parent_Type_Enum.Issue,
  IssueViewer: Parent_Type_Enum.Issue,
  // COI-639 (1786031870000_coi-639-oversight-role-types). Worth noting as the
  // first live test of this map: these arrived as Owner/Reader roles holding
  // seven grants, alphabetically-first `control_test_oversight_result`, which is
  // also absent from `useEntityInfo` — so under the old `resourceTypes[0]`
  // grouping they were a second instance of the COI-700 crash.
  OversightManager: Parent_Type_Enum.OversightAssessment,
  OversightViewer: Parent_Type_Enum.OversightAssessment,
  PolicyManager: Parent_Type_Enum.Document,
  PolicyViewer: Parent_Type_Enum.Document,
  RiskManager: Parent_Type_Enum.Risk,
  RiskViewer: Parent_Type_Enum.Risk,
  // COI-818. Manager-only by design — there is no `SettingsViewer` role type
  // for the Viewer column to find.
  SettingsManager: Parent_Type_Enum.Settings,
  ThirdPartyManager: Parent_Type_Enum.ThirdParty,
  ThirdPartyViewer: Parent_Type_Enum.ThirdParty,
};

export type AvailableRole = {
  roleKey: string;
  name: string;
  groupKey: Parent_Type_Enum;
  category: 'Manager' | 'Viewer';
};

type DefaultRole = {
  RoleKey: string;
  Name: string;
  Category: string;
  InstanceRoleKey?: null | string;
};

/**
 * Map the `auth_role_type` catalogue onto the rows of the Add/Edit Role
 * permissions table — one row per parent type, with a Manager and a Viewer
 * toggle.
 */
export const buildAvailableRoles = (
  roles: DefaultRole[] | undefined
): AvailableRole[] =>
  (roles ?? []).reduce<AvailableRole[]>((available, role) => {
    const groupKey = PERMISSION_GROUP_PARENT_TYPES[role.RoleKey];

    // Second gate on top of the map: under perm_v2 a NULL `InstanceRoleKey`
    // confers read-only regardless of the role's name, so offering such a role
    // as a Manager/Viewer toggle would misrepresent what granting it does.
    if (
      !groupKey ||
      (role.InstanceRoleKey !== 'Owner' && role.InstanceRoleKey !== 'Reader')
    ) {
      return available;
    }

    available.push({
      roleKey: role.RoleKey,
      name: role.Name,
      groupKey,
      category: role.Category === 'Manager' ? 'Manager' : 'Viewer',
    });

    return available;
  }, []);
